import { useState, useRef, useEffect, useMemo } from 'react'
import { Box, Grow } from '@mui/material'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import Button from '@app/stories/Button'
import Typography from '@app/stories/Typography'
import Icon from '@app/stories/Icons'
import { useCurrentUser } from '@app/context/CurrentUser'

import { BarChart } from '@mui/x-charts/BarChart'

import { useWhiteLabelingContext } from '@app/context/WhiteLabelingContext'

import { lightenHexColor } from '@app/utils/hexcodeUtils'

import { formatCurrency } from '@app/libs/formatters'

const planPaidChartData = [
  {
    placeOfService: 'Pharmacy',
    reporting: 332,
    benchmark: 268,
  },
  {
    placeOfService: 'Inpatient',
    reporting: 269,
    benchmark: 216,
  },
  {
    placeOfService: 'Outpatient',
    reporting: 415,
    benchmark: 366,
  },
  {
    placeOfService: 'Office Visit',
    reporting: 151,
    benchmark: 165,
  },
  {
    placeOfService: 'Post Acute',
    reporting: 0,
    benchmark: 20,
  },
]

const planPaidGridRows = [
  {
    id: 1,
    metric: 'Medical Claims Paid',
    reporting: '$725,115',
    benchmark: '--',
    comparison: '$0',
    deltaPercent: '0.00%',
  },
  {
    id: 2,
    metric: 'Pharmacy Claims Paid',
    reporting: '$288,133',
    benchmark: '--',
    comparison: '$0',
    deltaPercent: '0.00%',
  },
  {
    id: 3,
    metric: 'Dental Claims Paid',
    reporting: '$0',
    benchmark: '--',
    comparison: '$0',
    deltaPercent: '0.00%',
  },
  {
    id: 4,
    metric: 'Vision Claims Paid',
    reporting: '$0',
    benchmark: '--',
    comparison: '$0',
    deltaPercent: '0.00%',
  },
  {
    id: 5,
    metric: 'Total Health Plan Claims Paid',
    reporting: '$1,013,248',
    benchmark: '--',
    comparison: '$0',
    deltaPercent: '0.00%',
  },
  {
    id: 6,
    metric: 'Subscribers',
    reporting: 97,
    benchmark: '--',
    comparison: 0,
    deltaPercent: '0.00%',
  },
  {
    id: 7,
    metric: 'Members',
    reporting: 174,
    benchmark: '--',
    comparison: 0,
    deltaPercent: '0.00%',
  },
  {
    id: 8,
    metric: 'Employee Months',
    reporting: 868,
    benchmark: '--',
    comparison: 0,
    deltaPercent: '0.00%',
  },
  {
    id: 9,
    metric: 'Average Family Size',
    reporting: 1.79,
    benchmark: 1.9,
    comparison: 0.0,
    deltaPercent: '0.00%',
  },
  {
    id: 10,
    metric: 'Inpatient PEPM',
    reporting: '$269.13',
    benchmark: '$216.24',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 11,
    metric: 'Outpatient PEPM',
    reporting: '$414.79',
    benchmark: '$366.23',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 12,
    metric: 'Office Visit PEPM',
    reporting: '$151.26',
    benchmark: '$165.42',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 13,
    metric: 'Post Acute PEPM',
    reporting: '$0.20',
    benchmark: '$19.51',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 14,
    metric: 'Medical PEPM',
    reporting: '$835.39',
    benchmark: '$767.40',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 15,
    metric: 'Pharmacy PEPM',
    reporting: '$331.95',
    benchmark: '$268.05',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 16,
    metric: 'Dental PEPM',
    reporting: '$0.00',
    benchmark: '--',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 17,
    metric: 'Vision PEPM',
    reporting: '$0.00',
    benchmark: '--',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
  {
    id: 18,
    metric: 'Medical and Rx PEPM',
    reporting: '$1,167.34',
    benchmark: '$1,035.45',
    comparison: '$0.00',
    deltaPercent: '0.00%',
  },
]

const planPaidGridColumns = [
  { field: 'metric', headerName: 'Metric', flex: 2 },
  { field: 'reporting', headerName: 'Reporting', flex: 1 },
  { field: 'benchmark', headerName: 'Benchmark', flex: 1 },
  { field: 'comparison', headerName: 'Comparison', flex: 1 },
  { field: 'deltaPercent', headerName: '%Δ', flex: 1 },
]

const topPaidDrugsChartData = [
  {
    drug: 'Trulicity',
    paidAmount: 31312,
  },
  {
    drug: 'Ozempic',
    paidAmount: 29655,
  },
  {
    drug: 'Eliquis',
    paidAmount: 16400,
  },
  {
    drug: 'Mounjaro',
    paidAmount: 15262,
  },
  {
    drug: 'Farxiga',
    paidAmount: 14982,
  },
]

const top20DrugsTableColumns = [
  { field: 'brandName', headerName: 'Brand Name', width: 180 },
  { field: 'scripts', headerName: 'Scripts', type: 'number', width: 100 },
  { field: 'billed', headerName: 'Billed', width: 100 },
  { field: 'allowed', headerName: 'Allowed', width: 120 },
  { field: 'paid', headerName: 'Paid', width: 120 },
  { field: 'memberResponsibility', headerName: 'Member', width: 120 },
]

const top20DrugsTableRows = [
  {
    id: 1,
    brandName: 'Trulicity',
    scripts: 22,
    billed: '$6',
    allowed: '$31,312',
    paid: '$31,312',
    memberResponsibility: '$0',
  },
  {
    id: 2,
    brandName: 'Ozempic',
    scripts: 26,
    billed: '$8',
    allowed: '$29,655',
    paid: '$29,655',
    memberResponsibility: '$0',
  },
  {
    id: 3,
    brandName: 'Eliquis',
    scripts: 23,
    billed: '$7',
    allowed: '$16,400',
    paid: '$16,400',
    memberResponsibility: '$0',
  },
  {
    id: 4,
    brandName: 'Mounjaro',
    scripts: 13,
    billed: '$4',
    allowed: '$15,262',
    paid: '$15,262',
    memberResponsibility: '$0',
  },
  {
    id: 5,
    brandName: 'Farxiga',
    scripts: 21,
    billed: '$6',
    allowed: '$14,982',
    paid: '$14,982',
    memberResponsibility: '$0',
  },
  {
    id: 6,
    brandName: 'Januvia',
    scripts: 17,
    billed: '$3',
    allowed: '$14,417',
    paid: '$14,417',
    memberResponsibility: '$0',
  },
  {
    id: 7,
    brandName: 'Trelegy Ellipta',
    scripts: 13,
    billed: '$3',
    allowed: '$13,137',
    paid: '$13,137',
    memberResponsibility: '$0',
  },
  {
    id: 8,
    brandName: 'Jardiance',
    scripts: 12,
    billed: '$3',
    allowed: '$11,515',
    paid: '$11,515',
    memberResponsibility: '$0',
  },
  {
    id: 9,
    brandName: 'Breo Ellipta',
    scripts: 16,
    billed: '$5',
    allowed: '$8,436',
    paid: '$8,436',
    memberResponsibility: '$0',
  },
  {
    id: 10,
    brandName: 'Tresiba Flextouch U-100',
    scripts: 12,
    billed: '$4',
    allowed: '$6,821',
    paid: '$6,821',
    memberResponsibility: '$0',
  },
  {
    id: 11,
    brandName: 'Ajovy Autoinjector',
    scripts: 4,
    billed: '$0',
    allowed: '$6,816',
    paid: '$6,816',
    memberResponsibility: '$0',
  },
  {
    id: 12,
    brandName: 'Entresto',
    scripts: 9,
    billed: '$3',
    allowed: '$5,982',
    paid: '$5,802',
    memberResponsibility: '$180',
  },
  {
    id: 13,
    brandName: 'Miebo',
    scripts: 7,
    billed: '$2',
    allowed: '$5,230',
    paid: '$5,030',
    memberResponsibility: '$200',
  },
  {
    id: 14,
    brandName: 'Janumet Xr',
    scripts: 9,
    billed: '$3',
    allowed: '$4,476',
    paid: '$4,476',
    memberResponsibility: '$0',
  },
  {
    id: 15,
    brandName: 'Envarsus Xr',
    scripts: 7,
    billed: '$2',
    allowed: '$4,478',
    paid: '$4,178',
    memberResponsibility: '$300',
  },
  {
    id: 16,
    brandName: 'Janumet',
    scripts: 5,
    billed: '$1',
    allowed: '$4,132',
    paid: '$4,132',
    memberResponsibility: '$0',
  },
  {
    id: 17,
    brandName: 'Paxlovid',
    scripts: 5,
    billed: '$2',
    allowed: '$6,957',
    paid: '$4,114',
    memberResponsibility: '$2,843',
  },
  {
    id: 18,
    brandName: 'Brilinta',
    scripts: 9,
    billed: '$3',
    allowed: '$3,859',
    paid: '$3,859',
    memberResponsibility: '$0',
  },
  {
    id: 19,
    brandName: 'Anoro Ellipta',
    scripts: 4,
    billed: '$1',
    allowed: '$3,563',
    paid: '$3,563',
    memberResponsibility: '$0',
  },
  {
    id: 20,
    brandName: 'Semglee (Yfgn) Pen',
    scripts: 7,
    billed: '$2',
    allowed: '$3,453',
    paid: '$3,453',
    memberResponsibility: '$0',
  },
  {
    id: 21,
    brandName: 'Other',
    scripts: 1823,
    billed: '$493',
    allowed: '$84,627',
    paid: '$80,773',
    memberResponsibility: '$3,854',
  },
]

const top20ProvidersChartData = [
  {
    provider: 'CARILION ROCKBRIDGE COMM HOSP',
    paidAmount: 262959,
  },
  {
    provider: 'UVA HEALTH SCIENCES CTR',
    paidAmount: 235507,
  },
  {
    provider: 'ROBERT BRENNAN M D',
    paidAmount: 24257,
  },
  {
    provider: 'CARILION ROANOKE MEM HOSP',
    paidAmount: 138867,
  },
  {
    provider: 'LYNCHBURG GENERAL HOSPITAL',
    paidAmount: 61921,
  },
]

const top20ProvidersTableColumns = [
  { field: 'provider', headerName: 'Provider', width: 300 },
  { field: 'claims', headerName: 'Claims', type: 'number', width: 100 },
  { field: 'billed', headerName: 'Billed', width: 120 },
  { field: 'notCovered', headerName: 'Not Covered', width: 120 },
  { field: 'ppoSavings', headerName: 'PPO Savings', width: 120 },
  { field: 'allowed', headerName: 'Allowed', width: 120 },
  { field: 'paid', headerName: 'Paid', width: 120 },
  { field: 'member', headerName: 'Member', width: 120 },
]

const top20ProvidersTableRows = [
  {
    id: 1,
    provider: 'CARILION ROCKBRIDGE COMM HOSP',
    claims: 81,
    billed: '$454,453',
    notCovered: '$0',
    ppoSavings: '$75,929',
    allowed: '$304,691',
    paid: '$262,959',
    member: '$41,731',
  },
  {
    id: 2,
    provider: 'UVA HEALTH SCIENCES CTR',
    claims: 53,
    billed: '$429,934',
    notCovered: '$0',
    ppoSavings: '$164,294',
    allowed: '$249,704',
    paid: '$235,507',
    member: '$14,198',
  },
  {
    id: 3,
    provider: 'ROBERT BRENNAN M D',
    claims: 4,
    billed: '$63,831',
    notCovered: '$0',
    ppoSavings: '$36,370',
    allowed: '$27,429',
    paid: '$24,257',
    member: '$3,172',
  },
  {
    id: 4,
    provider: 'CARILION ROANOKE MEM HOSP',
    claims: 7,
    billed: '$25,382',
    notCovered: '$0',
    ppoSavings: '$7,092',
    allowed: '$18,236',
    paid: '$14,294',
    member: '$3,943',
  },
  {
    id: 5,
    provider: 'LYNCHBURG GENERAL HOSPITAL',
    claims: 1,
    billed: '$20,342',
    notCovered: '$0',
    ppoSavings: '$6,082',
    allowed: '$14,260',
    paid: '$13,867',
    member: '$393',
  },
  {
    id: 6,
    provider: 'AUGUSTA MEDICAL CENTER',
    claims: 49,
    billed: '$52,318',
    notCovered: '$0',
    ppoSavings: '$32,534',
    allowed: '$19,252',
    paid: '$13,032',
    member: '$6,221',
  },
  {
    id: 7,
    provider: 'JULIANA BUENO MD',
    claims: 6,
    billed: '$19,939',
    notCovered: '$0',
    ppoSavings: '$1,980',
    allowed: '$11,218',
    paid: '$11,218',
    member: '$0',
  },
  {
    id: 8,
    provider: 'MICHAEL HANLEY',
    claims: 2,
    billed: '$16,340',
    notCovered: '$0',
    ppoSavings: '$2,230',
    allowed: '$12,137',
    paid: '$11,209',
    member: '$928',
  },
  {
    id: 9,
    provider: 'THOMAS DRUZGAL MD',
    claims: 1,
    billed: '$7,691',
    notCovered: '$0',
    ppoSavings: '$1,154',
    allowed: '$6,537',
    paid: '$6,537',
    member: '$0',
  },
  {
    id: 10,
    provider: 'KENAN YOUNT',
    claims: 1,
    billed: '$8,843',
    notCovered: '$0',
    ppoSavings: '$3,777',
    allowed: '$5,606',
    paid: '$5,066',
    member: '$244',
  },
  {
    id: 11,
    provider: 'SCOTT BRIAN DUBIT',
    claims: 26,
    billed: '$9,008',
    notCovered: '$0',
    ppoSavings: '$2,383',
    allowed: '$5,606',
    paid: '$4,846',
    member: '$244',
  },
  {
    id: 12,
    provider: 'SOHIL PATEL MD',
    claims: 1,
    billed: '$7,481',
    notCovered: '$0',
    ppoSavings: '$1,122',
    allowed: '$6,359',
    paid: '$4,738',
    member: '$1,621',
  },
  {
    id: 13,
    provider: 'THOMAS FLOYD',
    claims: 1,
    billed: '$6,135',
    notCovered: '$0',
    ppoSavings: '$1,651',
    allowed: '$4,485',
    paid: '$3,460',
    member: '$1,819',
  },
  {
    id: 14,
    provider: 'MATTHEW E BRYANT',
    claims: 45,
    billed: '$9,638',
    notCovered: '$0',
    ppoSavings: '$4,253',
    allowed: '$3,840',
    paid: '$3,460',
    member: '$1,819',
  },
  {
    id: 15,
    provider: 'AMERICAN SPECIALTY PHYSICAL MEDICINE INC',
    claims: 43,
    billed: '$5,050',
    notCovered: '$0',
    ppoSavings: '$5,050',
    allowed: '$5,050',
    paid: '$1,819',
    member: '$1,819',
  },
  {
    id: 16,
    provider: 'BARBARA J TRANDEL',
    claims: 31,
    billed: '$8,423',
    notCovered: '$0',
    ppoSavings: '$3,059',
    allowed: '$7,100',
    paid: '$3,100',
    member: '$667',
  },
  {
    id: 17,
    provider: 'PATRICIA LEE SCHIRMER',
    claims: 21,
    billed: '$4,364',
    notCovered: '$0',
    ppoSavings: '$1,126',
    allowed: '$2,973',
    paid: '$2,874',
    member: '$101',
  },
  {
    id: 18,
    provider: 'EXACT SCIENCES LABORATORIES LLC',
    claims: 6,
    billed: '$3,908',
    notCovered: '$0',
    ppoSavings: '$861',
    allowed: '$2,972',
    paid: '$2,874',
    member: '$334',
  },
  {
    id: 19,
    provider: 'CAROLA TANNA',
    claims: 15,
    billed: '$3,998',
    notCovered: '$0',
    ppoSavings: '$1,182',
    allowed: '$2,721',
    paid: '$2,387',
    member: '$334',
  },
  {
    id: 20,
    provider: 'WILLIAM LYLE MCCLUNG',
    claims: 24,
    billed: '$3,412',
    notCovered: '$0',
    ppoSavings: '$429',
    allowed: '$2,651',
    paid: '$2,222',
    member: '$222',
  },
  {
    id: 21,
    provider: 'Other',
    claims: 1306,
    billed: '$395,854',
    notCovered: '$0',
    ppoSavings: '$159,659',
    allowed: '$153,368',
    paid: '$93,240',
    member: '$60,129',
  },
]

const top20PlacesOfServiceComparisonData = [
  {
    placeOfService: 'On Campus-Outpatient Hospital',
    totalPaidAmount: 337483,
    pepm: 388.81,
    benchmark: 258.34,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Inpatient Hospital',
    totalPaidAmount: 233609,
    pepm: 269.13,
    benchmark: 214.47,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Office',
    totalPaidAmount: 127368,
    pepm: 146.74,
    benchmark: 155.81,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Independent Laboratory',
    totalPaidAmount: 5298,
    pepm: 6.1,
    benchmark: 8.51,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Emergency Room - Hospital',
    totalPaidAmount: 3851,
    pepm: 4.44,
    benchmark: 42.24,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Ambulatory Surgical Center',
    totalPaidAmount: 3650,
    pepm: 4.09,
    benchmark: 34.95,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: `Telehealth Provided Other than in Patient's Home`,
    totalPaidAmount: 2831,
    pepm: 3.26,
    benchmark: 6.32,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Other Place of Service',
    totalPaidAmount: 2653,
    pepm: 3.06,
    benchmark: 5.58,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Urgent Care Facility',
    totalPaidAmount: 2628,
    pepm: 3.03,
    benchmark: 3.76,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Pharmacy',
    totalPaidAmount: 1913,
    pepm: 2.2,
    benchmark: 0.13,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Off Campus-Outpatient Hospital',
    totalPaidAmount: 1704,
    pepm: 1.96,
    benchmark: 1.6,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Ambulance - Land',
    totalPaidAmount: 595,
    pepm: 1.1,
    benchmark: 3.06,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Independent Clinic',
    totalPaidAmount: 880,
    pepm: 1.01,
    benchmark: 0.32,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Public Health Clinic',
    totalPaidAmount: 217,
    pepm: 0.25,
    benchmark: 0.07,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Home',
    totalPaidAmount: 177,
    pepm: 0.2,
    benchmark: 18.21,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    placeOfService: 'Nursing Facility',
    totalPaidAmount: 0,
    pepm: 0.0,
    benchmark: 0.05,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
]

const top20PlacesOfServiceTableColumns = [
  { field: 'placeOfService', headerName: 'Place of Service', width: 300 },
  { field: 'totalPaidAmount', headerName: 'Total Paid Amount ($)', type: 'number', width: 200 },
  { field: 'pepm', headerName: 'PEPM (Reporting Period)', type: 'number', width: 180 },
  { field: 'benchmark', headerName: 'Benchmark', type: 'number', width: 150 },
  {
    field: 'totalPaidAmountComparison',
    headerName: 'Total Paid Amount (Comparison Period)',
    type: 'number',
    width: 250,
  },
  { field: 'pepmComparison', headerName: 'PEPM (Comparison Period)', type: 'number', width: 180 },
  { field: 'percentageChange', headerName: '% Change', type: 'number', width: 150 },
  { field: 'priorPeriodRank', headerName: 'Prior Period Rank', width: 150 },
]

const top20PlacesOfServiceTableRows = [
  {
    id: 1,
    placeOfService: 'On Campus-Outpatient Hospital',
    totalPaidAmount: 337483,
    pepm: 388.81,
    benchmark: 258.34,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 2,
    placeOfService: 'Inpatient Hospital',
    totalPaidAmount: 233609,
    pepm: 269.13,
    benchmark: 214.47,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 3,
    placeOfService: 'Office',
    totalPaidAmount: 127368,
    pepm: 146.74,
    benchmark: 155.81,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 4,
    placeOfService: 'Independent Laboratory',
    totalPaidAmount: 5298,
    pepm: 6.1,
    benchmark: 8.51,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 5,
    placeOfService: 'Emergency Room - Hospital',
    totalPaidAmount: 3851,
    pepm: 4.44,
    benchmark: 42.24,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 6,
    placeOfService: 'Ambulatory Surgical Center',
    totalPaidAmount: 3650,
    pepm: 4.09,
    benchmark: 34.95,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 7,
    placeOfService: `Telehealth Provided Other than in Patient's Home`,
    totalPaidAmount: 2831,
    pepm: 3.26,
    benchmark: 6.32,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 8,
    placeOfService: 'Other Place of Service',
    totalPaidAmount: 2653,
    pepm: 3.06,
    benchmark: 5.58,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 9,
    placeOfService: 'Urgent Care Facility',
    totalPaidAmount: 2628,
    pepm: 3.03,
    benchmark: 3.76,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 10,
    placeOfService: 'Pharmacy',
    totalPaidAmount: 1913,
    pepm: 2.2,
    benchmark: 0.13,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 11,
    placeOfService: 'Off Campus-Outpatient Hospital',
    totalPaidAmount: 1704,
    pepm: 1.96,
    benchmark: 1.6,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 12,
    placeOfService: 'Ambulance - Land',
    totalPaidAmount: 595,
    pepm: 1.1,
    benchmark: 3.06,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 13,
    placeOfService: 'Independent Clinic',
    totalPaidAmount: 880,
    pepm: 1.01,
    benchmark: 0.32,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 14,
    placeOfService: 'Public Health Clinic',
    totalPaidAmount: 217,
    pepm: 0.25,
    benchmark: 0.07,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 15,
    placeOfService: 'Home',
    totalPaidAmount: 177,
    pepm: 0.2,
    benchmark: 18.21,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
  {
    id: 16,
    placeOfService: 'Nursing Facility',
    totalPaidAmount: 0,
    pepm: 0.0,
    benchmark: 0.05,
    totalPaidAmountComparison: 0,
    pepmComparison: 0,
    percentageChange: 0,
    priorPeriodRank: 'N/A',
  },
]

const topHighCostMembersTableRows = [
  {
    id: 1,
    sn: 1,
    memberId: 'JXM0838343200',
    relClass: 'Employee',
    gender: 'Male',
    age: 55,
    eligibility: 'Active',
    highestPaidDiagnosis: 'Cardiac Disorders',
    riskScore: 7.0,
    medicalPaidAmount: 151291.01,
    pharmacyPaidAmount: 933.58,
    totalPaidAmount: 152224.59,
  },
  {
    id: 2,
    sn: 2,
    memberId: 'RHQ6555065901',
    relClass: 'Spouse',
    gender: 'Male',
    age: 79,
    eligibility: 'Active',
    highestPaidDiagnosis: 'Musculoskeletal Disorders',
    riskScore: 4.94,
    medicalPaidAmount: 123343.05,
    pharmacyPaidAmount: 574.79,
    totalPaidAmount: 123917.84,
  },
  {
    id: 3,
    sn: 3,
    memberId: 'KHK1125462000',
    relClass: 'Employee',
    gender: 'Male',
    age: 69,
    eligibility: 'Active',
    highestPaidDiagnosis: 'Cancer',
    riskScore: 4.6,
    medicalPaidAmount: 68821.85,
    pharmacyPaidAmount: 1985.74,
    totalPaidAmount: 70802.59,
  },
  {
    id: 4,
    sn: 'Total',
    memberId: '',
    relClass: '',
    gender: '',
    age: '',
    eligibility: '',
    highestPaidDiagnosis: '',
    riskScore: '',
    medicalPaidAmount: 343450.91,
    pharmacyPaidAmount: 3494.11,
    totalPaidAmount: 346945.02,
  },
]

const topHighCostMembersTableColumns = [
  { field: 'sn', headerName: 'SN', width: 100 },
  { field: 'memberId', headerName: 'Member ID', width: 200 },
  { field: 'relClass', headerName: 'Rel Class', width: 150 },
  { field: 'gender', headerName: 'Gender', width: 150 },
  { field: 'age', headerName: 'Age', type: 'number', width: 100 },
  { field: 'eligibility', headerName: 'Eligibility As of 03/31/25', width: 200 },
  { field: 'highestPaidDiagnosis', headerName: 'Highest Paid Diagnosis', width: 250 },
  { field: 'riskScore', headerName: 'Risk Score', type: 'number', width: 150 },
  { field: 'medicalPaidAmount', headerName: 'Medical Paid Amount', type: 'number', width: 200 },
  { field: 'pharmacyPaidAmount', headerName: 'Pharmacy Paid Amount', type: 'number', width: 200 },
  { field: 'totalPaidAmount', headerName: 'Total Paid Amount', type: 'number', width: 200 },
]

const trendAndVarianceSummaryTableColumns = [
  { field: 'measures', headerName: 'Measures', width: 250 },
  { field: 'units', headerName: 'Units', width: 120 },
  {
    field: 'reportingTotalCount',
    headerName: 'Reporting Total Count',
    type: 'number',
    width: 180,
  },
  {
    field: 'reportingPaidPerUnit',
    headerName: 'Reporting Paid Per Unit',
    type: 'number',
    width: 220,
  },
  { field: 'reportingPEPM', headerName: 'Reporting PEPM', type: 'number', width: 180 },
  {
    field: 'comparisonTotalCount',
    headerName: 'Comparison Total Count',
    type: 'number',
    width: 220,
  },
  {
    field: 'comparisonPaidPerUnit',
    headerName: 'Comparison Paid Per Unit',
    type: 'number',
    width: 220,
  },
  { field: 'comparisonPEPM', headerName: 'Comparison PEPM', type: 'number', width: 220 },
  { field: 'varianceTotalCount', headerName: 'Variance Total Count', type: 'number', width: 220 },
  {
    field: 'variancePaidPerUnit',
    headerName: 'Variance Paid Per Unit',
    type: 'number',
    width: 220,
  },
  { field: 'variancePEPM', headerName: 'Variance PEPM', type: 'number', width: 220 },
  { field: 'trendTotalCount', headerName: 'Trend Total Count', type: 'number', width: 220 },
  { field: 'trendPaidPerUnit', headerName: 'Trend Paid Per Unit', type: 'number', width: 220 },
  { field: 'trendPEPM', headerName: 'Trend PEPM', type: 'number', width: 220 },
]

const trendAndVarianceSummaryTableRows = [
  {
    id: 1,
    measures: 'Inpatient Maternity',
    units: 'Admits',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 2,
    measures: 'Inpatient Medical',
    units: 'Admits',
    reportingTotalCount: 2,
    reportingPaidPerUnit: 23132.2,
    reportingPEPM: 53.3,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.01,
    trendPaidPerUnit: 324.04,
    trendPEPM: 3.34,
  },
  {
    id: 3,
    measures: 'Inpatient Substance Abuse',
    units: 'Admits',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 4,
    measures: 'Inpatient Mental Health',
    units: 'Admits',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 5,
    measures: 'Inpatient Surgical',
    units: 'Admits',
    reportingTotalCount: 2,
    reportingPaidPerUnit: 93991.85,
    reportingPEPM: 216.57,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.01,
    trendPaidPerUnit: 1643.42,
    trendPEPM: 17.06,
  },
  {
    id: 6,
    measures: 'All Other Inpatient',
    units: 'Claims',
    reportingTotalCount: 1,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.01,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 7,
    measures: 'Colonoscopies',
    units: 'Services',
    reportingTotalCount: 9,
    reportingPaidPerUnit: 4973.31,
    reportingPEPM: 40.11,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.05,
    trendPaidPerUnit: 207.19,
    trendPEPM: 2.56,
  },
  {
    id: 8,
    measures: 'Outpatient Services',
    units: 'Visits',
    reportingTotalCount: 9,
    reportingPaidPerUnit: 6742.03,
    reportingPEPM: 69.91,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.07,
    trendPaidPerUnit: 245.31,
    trendPEPM: 4.94,
  },
  {
    id: 9,
    measures: 'ASC Services',
    units: 'Visits',
    reportingTotalCount: 1,
    reportingPaidPerUnit: 2298.06,
    reportingPEPM: 2.65,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.01,
    trendPaidPerUnit: 18.98,
    trendPEPM: 0.2,
  },
  {
    id: 10,
    measures: 'Emergency Room',
    units: 'Visits',
    reportingTotalCount: 23,
    reportingPaidPerUnit: 2337.7,
    reportingPEPM: 61.94,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.18,
    trendPaidPerUnit: 149.42,
    trendPEPM: 4.11,
  },
  {
    id: 11,
    measures: 'Outpatient Urgent Care',
    units: 'Visits',
    reportingTotalCount: 55,
    reportingPaidPerUnit: 47.78,
    reportingPEPM: 3.03,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.38,
    trendPaidPerUnit: 3.39,
    trendPEPM: 0.2,
  },
  {
    id: 12,
    measures: 'Radiology - Complex (PET)',
    units: 'Services',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 13,
    measures: 'Radiology - Complex (OP Hospital)',
    units: 'Services',
    reportingTotalCount: 41,
    reportingPaidPerUnit: 1177.37,
    reportingPEPM: 55.61,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.32,
    trendPaidPerUnit: 52.32,
    trendPEPM: 4.09,
  },
  {
    id: 14,
    measures: 'Radiology - Complex (Other)',
    units: 'Services',
    reportingTotalCount: 15,
    reportingPaidPerUnit: 2300.54,
    reportingPEPM: 39.76,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.11,
    trendPaidPerUnit: 113.29,
    trendPEPM: 2.81,
  },
  {
    id: 15,
    measures: 'Dialysis Facility',
    units: 'Services',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 16,
    measures: 'Physician - PCP Visit',
    units: 'Visits',
    reportingTotalCount: 2,
    reportingPaidPerUnit: 299.38,
    reportingPEPM: 0.69,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 5.99,
    trendPEPM: 0.0,
  },
  {
    id: 17,
    measures: 'Physician - Specialist Visit',
    units: 'Visits',
    reportingTotalCount: 358,
    reportingPaidPerUnit: 106.49,
    reportingPEPM: 43.92,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.07,
    trendPaidPerUnit: 253.31,
    trendPEPM: 4.94,
  },
  {
    id: 18,
    measures: 'Physician - Consultation',
    units: 'Visits',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 19,
    measures: 'Physician - Preventive',
    units: 'Visits',
    reportingTotalCount: 65,
    reportingPaidPerUnit: 211.78,
    reportingPEPM: 15.86,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.07,
    trendPaidPerUnit: 211.88,
    trendPEPM: 0.93,
  },
  {
    id: 20,
    measures: 'Physical Therapy',
    units: 'Services',
    reportingTotalCount: 56,
    reportingPaidPerUnit: 153.39,
    reportingPEPM: 9.9,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.42,
    trendPaidPerUnit: 9.79,
    trendPEPM: 0.62,
  },
  {
    id: 21,
    measures: 'Physician - Mental Health',
    units: 'Visits',
    reportingTotalCount: 77,
    reportingPaidPerUnit: 67.06,
    reportingPEPM: 5.95,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 3.84,
    trendPEPM: 0.38,
  },
  {
    id: 22,
    measures: 'Physician - Substance Abuse',
    units: 'Visits',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 23,
    measures: 'Physician - Telehealth',
    units: 'Claims',
    reportingTotalCount: 8,
    reportingPaidPerUnit: 97.12,
    reportingPEPM: 0.9,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.05,
    trendPaidPerUnit: 3.83,
    trendPEPM: 0.0,
  },
  {
    id: 24,
    measures: 'SNF',
    units: 'Days',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 25,
    measures: 'Physician - Chiro',
    units: 'Claims',
    reportingTotalCount: 45,
    reportingPaidPerUnit: 11.83,
    reportingPEPM: 0.61,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.32,
    trendPaidPerUnit: 0.59,
    trendPEPM: 0.0,
  },
  {
    id: 26,
    measures: 'Physician - Mental Health (Claims)',
    units: 'Claims',
    reportingTotalCount: 7,
    reportingPaidPerUnit: 48.89,
    reportingPEPM: 0.39,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.06,
    trendPaidPerUnit: 2.25,
    trendPEPM: 0.1,
  },
  {
    id: 27,
    measures: 'Physician - Substance Abuse (Claims)',
    units: 'Claims',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 28,
    measures: 'Physician - Maternity',
    units: 'Claims',
    reportingTotalCount: 12,
    reportingPaidPerUnit: 92.77,
    reportingPEPM: 1.28,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 29,
    measures: 'Physician - Immunizations',
    units: 'Claims',
    reportingTotalCount: 8,
    reportingPaidPerUnit: 61.86,
    reportingPEPM: 0.57,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.02,
    trendPEPM: 0.0,
  },
  {
    id: 30,
    measures: 'Physician - Anesthesia',
    units: 'Claims',
    reportingTotalCount: 3,
    reportingPaidPerUnit: 9.92,
    reportingPEPM: 0.03,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 31,
    measures: 'Radiology - Routine',
    units: 'Claims',
    reportingTotalCount: 85,
    reportingPaidPerUnit: 159.43,
    reportingPEPM: 15.61,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.66,
    trendPaidPerUnit: 9.38,
    trendPEPM: 1.24,
  },
  {
    id: 32,
    measures: 'Lab/ Pathology',
    units: 'Claims',
    reportingTotalCount: 216,
    reportingPaidPerUnit: 55.97,
    reportingPEPM: 13.93,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 1.52,
    trendPaidPerUnit: 3.38,
    trendPEPM: 0.94,
  },
  {
    id: 33,
    measures: 'DME',
    units: 'Claims',
    reportingTotalCount: 12,
    reportingPaidPerUnit: 13.26,
    reportingPEPM: 0.18,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.1,
    trendPaidPerUnit: 0.53,
    trendPEPM: 0.02,
  },
  {
    id: 34,
    measures: 'Physician - Surgery',
    units: 'Claims',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 35,
    measures: 'Ambulance',
    units: 'Claims',
    reportingTotalCount: 1,
    reportingPaidPerUnit: 955.31,
    reportingPEPM: 1.1,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 36,
    measures: 'Hospice',
    units: 'Claims',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 37,
    measures: 'Dental',
    units: 'Claims',
    reportingTotalCount: 1,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.01,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 38,
    measures: 'Chemotherapy',
    units: 'Claims',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 39,
    measures: 'Injectable Drugs',
    units: 'Claims',
    reportingTotalCount: 21,
    reportingPaidPerUnit: 6726.17,
    reportingPEPM: 162.73,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.14,
    trendPaidPerUnit: 491.23,
    trendPEPM: 10.78,
  },
  {
    id: 40,
    measures: 'Physician - Other',
    units: 'Claims',
    reportingTotalCount: 183,
    reportingPaidPerUnit: 28.8,
    reportingPEPM: 6.07,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 1.31,
    trendPaidPerUnit: 2.01,
    trendPEPM: 0.44,
  },
  {
    id: 41,
    measures: 'Outpatient Other',
    units: 'Claims',
    reportingTotalCount: 40,
    reportingPaidPerUnit: 351.04,
    reportingPEPM: 16.18,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.3,
    trendPaidPerUnit: 25.26,
    trendPEPM: 1.26,
  },
  {
    id: 42,
    measures: 'Non - Outpatient Rx',
    units: 'Claims',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 43,
    measures: 'All Other',
    units: 'Claims',
    reportingTotalCount: 208,
    reportingPaidPerUnit: 18.97,
    reportingPEPM: 4.55,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 1.68,
    trendPaidPerUnit: 58.32,
    trendPEPM: 3.32,
  },
  {
    id: 44,
    measures: 'Rx - Specialty Retail Maint',
    units: 'Scripts',
    reportingTotalCount: 47,
    reportingPaidPerUnit: 1010.18,
    reportingPEPM: 54.5,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.32,
    trendPaidPerUnit: 58.92,
    trendPEPM: 3.32,
  },
  {
    id: 45,
    measures: 'Rx - Specialty Retail Acute',
    units: 'Scripts',
    reportingTotalCount: 33,
    reportingPaidPerUnit: 293.75,
    reportingPEPM: 11.17,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.9,
    trendPaidPerUnit: 2.37,
    trendPEPM: 0.39,
  },
  {
    id: 46,
    measures: 'Rx - Generic Retail Maint',
    units: 'Scripts',
    reportingTotalCount: 1102,
    reportingPaidPerUnit: 34.95,
    reportingPEPM: 44.37,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 7.05,
    trendPaidPerUnit: 2.11,
    trendPEPM: 2.65,
  },
  {
    id: 47,
    measures: 'Rx - Generic Retail Acute',
    units: 'Scripts',
    reportingTotalCount: 420,
    reportingPaidPerUnit: 14.3,
    reportingPEPM: 6.92,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 2.76,
    trendPaidPerUnit: 0.78,
    trendPEPM: 0.39,
  },
  {
    id: 48,
    measures: 'Rx - Brand Retail Maint Formulary',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 49,
    measures: 'Rx - Brand Retail Acute Injunctory',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 50,
    measures: 'Rx - Brand Retail Acute Non-Formulary',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 51,
    measures: 'Rx - Specialty Mail Order Maint',
    units: 'Scripts',
    reportingTotalCount: 10,
    reportingPaidPerUnit: 2731.28,
    reportingPEPM: 31.47,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.06,
    trendPaidPerUnit: 94.23,
    trendPEPM: 1.67,
  },
  {
    id: 52,
    measures: 'Rx - Specialty Mail Order Acute',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 53,
    measures: 'Rx - Generic Mail Order Maint',
    units: 'Scripts',
    reportingTotalCount: 138,
    reportingPaidPerUnit: 39.76,
    reportingPEPM: 6.32,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.32,
    trendPaidPerUnit: 58.58,
    trendPEPM: 3.33,
  },
  {
    id: 54,
    measures: 'Rx - Brand Mail Order Acute Formulary',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 55,
    measures: 'Rx - Brand Mail Order Acute Non-Formulary',
    units: 'Scripts',
    reportingTotalCount: 0,
    reportingPaidPerUnit: 0,
    reportingPEPM: 0,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 0.0,
    trendPaidPerUnit: 0.0,
    trendPEPM: 0.0,
  },
  {
    id: 56,
    measures: 'Rx - Other',
    units: 'Scripts',
    reportingTotalCount: 292,
    reportingPaidPerUnit: 524.29,
    reportingPEPM: 176.38,
    comparisonTotalCount: 0,
    comparisonPaidPerUnit: 0,
    comparisonPEPM: 0,
    varianceTotalCount: 0,
    variancePaidPerUnit: 0,
    variancePEPM: 0,
    trendTotalCount: 1.85,
    trendPaidPerUnit: 31.23,
    trendPEPM: 10.14,
  },
]

const aggregateExcessInsuranceTableColumns = [
  { field: 'month', headerName: 'Month', width: 100 },
  { field: 'numberOfEmployees', headerName: '# of Employees', type: 'number', width: 150 },
  { field: 'eeSp', headerName: 'EE + SP', type: 'number', width: 70 },
  { field: 'eeCh', headerName: 'EE + CH', type: 'number', width: 70 },
  { field: 'family', headerName: 'Family', type: 'number', width: 70 },
  {
    field: 'monthlyAttachmentPoint',
    headerName: 'Monthly Attachment Point',
    type: 'number',
    width: 220,
  },
  {
    field: 'yearToDateAttachmentPoint',
    headerName: 'Year to Date Attachment Point',
    type: 'number',
    width: 250,
  },
  { field: 'medicalClaimsPaid', headerName: 'Medical Claims Paid', type: 'number', width: 180 },
  { field: 'cignaRxClaimsPaid', headerName: 'Cigna Rx Claims Paid', type: 'number', width: 180 },
  { field: 'cCUFeesPaid', headerName: 'CCU Fees Paid', type: 'number', width: 150 },
  { field: 'monthlyPaidClaims', headerName: 'Monthly Paid Claims', type: 'number', width: 180 },
  {
    field: 'overSpecificDeductible',
    headerName: 'Over Specific Deductible',
    type: 'number',
    width: 200,
  },
  { field: 'aggAccomFunding', headerName: 'Agg Accom Funding', type: 'number', width: 180 },
  {
    field: 'yearToDateClaimsPaid',
    headerName: 'Year to Date Claims Paid',
    type: 'number',
    width: 200,
  },
  { field: 'aggregateLossRatio', headerName: 'Aggregate Loss Ratio', type: 'number', width: 180 },
]

const aggregateExcessInsuranceTableRows = [
  {
    id: 1,
    month: 'Nov, 2022',
    numberOfEmployees: 19,
    eeSp: 0,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,520.08',
    yearToDateAttachmentPoint: '$3,520.08',
    medicalClaimsPaid: '$942.32',
    cignaRxClaimsPaid: '$345.10',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$1,287.42',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$1,287.42',
    aggregateLossRatio: '37%',
  },
  {
    id: 2,
    month: 'Dec, 2022',
    numberOfEmployees: 21,
    eeSp: 0,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,730.86',
    yearToDateAttachmentPoint: '$7,250.94',
    medicalClaimsPaid: '$1,224.20',
    cignaRxClaimsPaid: '$620.72',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$1,844.92',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$3,132.34',
    aggregateLossRatio: '47%',
  },
  {
    id: 3,
    month: 'Jan, 2023',
    numberOfEmployees: 17,
    eeSp: 0,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,309.30',
    yearToDateAttachmentPoint: '$10,560.24',
    medicalClaimsPaid: '$1,869.34',
    cignaRxClaimsPaid: '$640.33',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$2,509.67',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$5,642.01',
    aggregateLossRatio: '53%',
  },
  {
    id: 4,
    month: 'Feb, 2023',
    numberOfEmployees: 21,
    eeSp: 0,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,730.86',
    yearToDateAttachmentPoint: '$14,291.10',
    medicalClaimsPaid: '$2,063.59',
    cignaRxClaimsPaid: '$651.55',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$2,715.14',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$8,357.15',
    aggregateLossRatio: '58%',
  },
  {
    id: 5,
    month: 'Mar, 2023',
    numberOfEmployees: 19,
    eeSp: 2,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,983.80',
    yearToDateAttachmentPoint: '$18,274.90',
    medicalClaimsPaid: '$1,228.09',
    cignaRxClaimsPaid: '$1,060.14',
    cCUFeesPaid: '$84.34',
    monthlyPaidClaims: '$2,372.57',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$10,729.72',
    aggregateLossRatio: '59%',
  },
  {
    id: 6,
    month: 'Apr, 2023',
    numberOfEmployees: 16,
    eeSp: 1,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,435.77',
    yearToDateAttachmentPoint: '$21,710.67',
    medicalClaimsPaid: '$4,977.13',
    cignaRxClaimsPaid: '$1,824.23',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$6,801.36',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$17,531.08',
    aggregateLossRatio: '81%',
  },
  {
    id: 7,
    month: 'May, 2023',
    numberOfEmployees: 20,
    eeSp: 1,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,857.33',
    yearToDateAttachmentPoint: '$25,568.00',
    medicalClaimsPaid: '$1,844.81',
    cignaRxClaimsPaid: '$360.17',
    cCUFeesPaid: '$8.83',
    monthlyPaidClaims: '$2,213.81',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$19,744.89',
    aggregateLossRatio: '77%',
  },
  {
    id: 8,
    month: 'Jun, 2023',
    numberOfEmployees: 19,
    eeSp: 1,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$3,751.94',
    yearToDateAttachmentPoint: '$29,319.94',
    medicalClaimsPaid: '$1,240.42',
    cignaRxClaimsPaid: '$2,389.11',
    cCUFeesPaid: '$128.83',
    monthlyPaidClaims: '$3,758.36',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$23,503.25',
    aggregateLossRatio: '80%',
  },
  {
    id: 9,
    month: 'Jul, 2023',
    numberOfEmployees: 15,
    eeSp: -1,
    eeCh: 3,
    family: 3,
    monthlyAttachmentPoint: '$2,866.66',
    yearToDateAttachmentPoint: '$32,186.60',
    medicalClaimsPaid: '$1,438.00',
    cignaRxClaimsPaid: '$565.40',
    cCUFeesPaid: '$0.00',
    monthlyPaidClaims: '$2,003.40',
    overSpecificDeductible: '$0.00',
    aggAccomFunding: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 10,
    month: 'Aug, 2023',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 11,
    month: 'Sep, 2023',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 12,
    month: 'Oct, 2023',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 13,
    month: 'Nov, 2023',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 14,
    month: 'Dec, 2023',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 15,
    month: 'Jan, 2024',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 16,
    month: 'Feb, 2024',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 17,
    month: 'Mar, 2024',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
  {
    id: 18,
    month: 'Apr, 2024',
    monthlyAttachmentPoint: '$0.00',
    yearToDateAttachmentPoint: '$32,186.60',
    monthlyPaidClaims: '$0.00',
    yearToDateClaimsPaid: '$25,506.65',
    aggregateLossRatio: '79%',
  },
]

const PlanAdminReportsContent = () => {
  const { planColorPrimary } = useWhiteLabelingContext()
  const lightenedColor = lightenHexColor(planColorPrimary, 50)
  const { managedPlans } = useCurrentUser()

  const [currentReportType, setCurrentReportType] = useState(
    'Plan Paid Amount by Service Category (PEPM)',
  )

  const reportTypes = useMemo(
    () => [
      {
        label: 'Plan Paid Amount by Service Category (PEPM)',
        value: {
          chartData: {
            dataset: planPaidChartData,
            xAxis: [
              {
                data: ['Pharmacy', 'Inpatient', 'Outpatient', 'Office Visit', 'Post Acute'],
                scaleType: 'band',
              },
            ],
            yAxis: [
              {
                type: 'value',
                name: 'Paid Amount ($)',
                valueFormatter: formatCurrency,
              },
            ],
            series: [
              {
                dataKey: 'reporting',
                label: 'Reporting',
                color: planColorPrimary,
                valueFormatter: formatCurrency,
              },
              {
                dataKey: 'benchmark',
                label: 'Benchmark',
                color: lightenedColor,
                valueFormatter: formatCurrency,
              },
            ],
            height: 800,
            margin: { top: 50, right: 50, bottom: 50, left: 80 },
            layout: 'vertical',
            uiLayout: 'column',
          },
          gridRows: planPaidGridRows,
          gridColumns: planPaidGridColumns,
          chartHeight: 800,
          dataGridHeight: 800,
        },
      },
      {
        label: 'Top Paid Drugs',
        value: {
          chartData: {
            dataset: topPaidDrugsChartData,
            yAxis: [
              {
                data: ['Trulicity', 'Ozempic', 'Eliquis', 'Mounjaro', 'Farxiga'],
                scaleType: 'band',
              },
            ],
            xAxis: [
              {
                type: 'value',
                name: 'Paid Amount ($)',
                valueFormatter: formatCurrency,
              },
            ],
            series: [
              {
                dataKey: 'paidAmount',
                label: 'Paid Amount ($)',
                color: planColorPrimary,
                valueFormatter: formatCurrency,
              },
            ],
            height: 400,
            margin: { top: 50, right: 50, bottom: 50, left: 80 },
            layout: 'horizontal',
            uiLayout: 'row',
          },
          gridRows: top20DrugsTableRows,
          gridColumns: top20DrugsTableColumns,
          chartHeight: 400,
          dataGridHeight: 400,
        },
      },
      {
        label: 'Top 20 Providers',
        value: {
          chartData: {
            dataset: top20ProvidersChartData,
            xAxis: [
              {
                data: [
                  'CARILION ROCKBRIDGE COMM HOSP',
                  'UVA HEALTH SCIENCES CTR',
                  'ROBERT BRENNAN M D',
                  'CARILION ROANOKE MEM HOSP',
                  'LYNCHBURG GENERAL HOSPITAL',
                ],
                scaleType: 'band',
              },
            ],
            yAxis: [
              {
                type: 'value',
                name: 'Paid Amount ($)',
                valueFormatter: formatCurrency,
              },
            ],
            series: [
              {
                dataKey: 'paidAmount',
                label: 'Paid Amount ($)',
                color: planColorPrimary,
                valueFormatter: formatCurrency,
              },
            ],
            margin: { top: 50, right: 50, bottom: 50, left: 100 },
            height: 400,
            layout: 'vertical',
            uiLayout: 'row',
          },
          gridRows: top20ProvidersTableRows,
          gridColumns: top20ProvidersTableColumns,
          chartHeight: 400,
          dataGridHeight: 400,
        },
      },
      {
        label: 'Top 20 Places of Service',
        value: {
          chartData: {
            dataset: top20PlacesOfServiceComparisonData,
            xAxis: [{ scaleType: 'band', dataKey: 'placeOfService' }],
            yAxis: [
              { scaleType: 'linear', dataKey: 'totalPaidAmount', valueFormatter: formatCurrency },
              {
                scaleType: 'band',
                dataKey: 'pepm',
                label: 'PEPM (Reporting Period)',
                valueFormatter: formatCurrency,
              },
            ],
            series: [
              {
                dataKey: 'totalPaidAmount',
                label: 'Total Paid Amount',
                color: planColorPrimary,
                valueFormatter: formatCurrency,
              },
              {
                dataKey: 'pepm',
                label: 'PEPM (Reporting Period)',
                color: lightenedColor,
                valueFormatter: formatCurrency,
              },
            ],
            margin: { top: 50, right: 50, bottom: 50, left: 100 },
            height: 400,
            layout: 'vertical',
            uiLayout: 'row',
          },
          gridRows: top20PlacesOfServiceTableRows,
          gridColumns: top20PlacesOfServiceTableColumns,
          chartHeight: 400,
          dataGridHeight: 400,
        },
      },
      {
        label: 'Top High Cost Members',
        value: {
          chartData: null,
          gridRows: topHighCostMembersTableRows,
          gridColumns: topHighCostMembersTableColumns,
          chartHeight: 0,
          dataGridHeight: 800,
        },
      },
      {
        label: 'Trend and Variance Summary',
        value: {
          chartData: null,
          gridRows: trendAndVarianceSummaryTableRows,
          gridColumns: trendAndVarianceSummaryTableColumns,
          chartHeight: 0,
          dataGridHeight: 800,
        },
      },
      {
        label: 'Aggregate Excess Insurance',
        value: {
          chartData: null,
          gridRows: aggregateExcessInsuranceTableRows,
          gridColumns: aggregateExcessInsuranceTableColumns,
          chartHeight: 0,
          dataGridHeight: 800,
        },
      },
    ],
    [planColorPrimary, lightenedColor],
  )

  const [chartData, setChartData] = useState({
    dataset: planPaidChartData,
    xAxis: [
      {
        data: ['Pharmacy', 'Inpatient', 'Outpatient', 'Office Visit', 'Post Acute'],
        scaleType: 'band',
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'Paid Amount ($)',
      },
    ],
    series: [
      { dataKey: 'reporting', label: 'Reporting', color: planColorPrimary },
      { dataKey: 'benchmark', label: 'Benchmark', color: lightenedColor },
    ],
    height: 800,
    layout: 'vertical',
    uiLayout: 'column',
  })

  const [rows, setRows] = useState(planPaidGridRows)
  const [columns, setColumns] = useState(planPaidGridColumns)
  const [chartHeight, setChartHeight] = useState(800)
  const [dataGridHeight, setDataGridHeight] = useState(800)

  const ReportTypeSelectionButton = ({ label, value, style }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef(null)

    useEffect(() => {
      const handleClickOutside = (event) => {
        console.log(event.target)
        if (event.target.classList.contains('doNotPush')) {
          return
        }
        // Close the menu if clicked outside
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setMenuOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    return (
      <Box display='flex' alignItems='center' position='relative' width='100%'>
        <Button
          variant={currentReportType === label ? 'contained' : 'text'}
          onClick={() => {
            setCurrentReportType(label)
            setChartData(value.chartData)
            setRows(value.gridRows)
            setColumns(value.gridColumns)
            setChartHeight(value.chartHeight)
            setDataGridHeight(value.dataGridHeight)
          }}
          style={{
            marginLeft: '0px',
            ...style,
          }}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            padding: '4px 56px 4px 24px',
            color: currentReportType === label ? 'white' : planColorPrimary,
            backgroundColor: currentReportType === label ? planColorPrimary : 'white',
            '&:hover': {
              backgroundColor: currentReportType === label ? planColorPrimary : '#F5F5F5',
            },
            '&.Mui-disabled': {
              backgroundColor: '#F5F5F5',
              color: '#BDBDBD',
            },
            display: 'flex',
            justifyContent: 'flex-start',
            textAlign: 'left',
            width: '100%',
          }}
        >
          {label}
        </Button>
        <Box
          ref={menuRef}
          position='absolute'
          right='8px'
          top='0'
          height='100%'
          display='flex'
          alignItems='center'
        >
          <Button
            variant='unstyled'
            style={{
              minWidth: '0px',
              padding: '0px',
              height: '40px',
              width: '40px',
              borderRadius: '20px',
              color: currentReportType === label ? 'white' : planColorPrimary,
            }}
            onClick={(event) => {
              event.stopPropagation()
              setMenuOpen((prev) => !prev)
            }}
          >
            <Icon name='moreVert' color='inherit' />
          </Button>
          <Grow in={menuOpen} style={{ transformOrigin: 'left' }}>
            <Box
              position='absolute'
              left='calc(100% + 16px)'
              top='-1px'
              bgcolor='#fff'
              border='1px solid #E0E0E0'
              borderRadius='8px'
              width='150px'
              height='min-content'
              zIndex={1}
            >
              <Box display='flex' flexDirection='column' alignItems='flex-start'>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => {
                    console.log('Download clicked')
                    setMenuOpen(false)
                  }}
                  style={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: planColorPrimary,
                    justifyContent: 'flex-start',
                    width: '100%',
                    marginLeft: '0px',
                    padding: '8px 16px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <Icon name='getApp' color='inherit' fontSize='small' />
                  Download
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => {
                    console.log('Rename clicked')
                    setMenuOpen(false)
                  }}
                  style={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: planColorPrimary,
                    justifyContent: 'flex-start',
                    width: '100%',
                    marginLeft: '0px',
                    padding: '8px 16px',
                    borderRadius: '0',
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <Icon name='editOutlined' color='inherit' fontSize='small' />
                  Rename
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => {
                    console.log('Print clicked')
                    setMenuOpen(false)
                  }}
                  style={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: planColorPrimary,
                    justifyContent: 'flex-start',
                    width: '100%',
                    marginLeft: '0px',
                    padding: '8px 16px',
                    borderRadius: '0 0 8px 8px',
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <Icon name='print' color='inherit' fontSize='small' />
                  Print
                </Button>
              </Box>
            </Box>
          </Grow>
        </Box>
      </Box>
    )
  }

  if (managedPlans?.managedPlanID !== 'XHReVVZRwgv162jnD0') {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height='100%'
        width='100%'
        padding='16px'
      >
        <Typography variant='h6' color='textSecondary'>
          Reports are not currently available for this plan.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      display='flex'
      gap='16px'
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        display='flex'
        flexDirection='column'
        width='300px'
        border='1px solid #E0E0E0'
        borderRadius='8px'
        height='fit-content'
        sx={{
          width: {
            xs: '100%',
            md: '300px',
          },
        }}
      >
        {reportTypes.map((reportType, index) => (
          <ReportTypeSelectionButton
            key={reportType.value}
            label={reportType.label}
            value={reportType.value}
            style={{
              marginBottom: currentReportType === reportType.label ? '1px' : '0px',
              outline:
                currentReportType === reportType.label ? `1px solid ${planColorPrimary}` : 'none',
              borderBottom:
                currentReportType === reportType.label || index === reportTypes.length - 1
                  ? 'none'
                  : '1px solid #E0E0E0',
              borderRadius:
                index === 0
                  ? '8px 8px 0 0'
                  : index === reportTypes.length - 1
                    ? '0 0 8px 8px'
                    : '0',
            }}
          />
        ))}
      </Box>
      <Box
        display='flex'
        padding='16px'
        borderRadius='8px'
        border='1px solid #E0E0E0'
        backgroundColor='white'
        sx={{
          width: { xs: '100%', md: 'calc(100% - 316px)' },
          flexDirection: {
            xs: 'column',
            md: chartData?.uiLayout === 'column' ? 'row' : 'column',
          },
          padding: '0px',
        }}
      >
        {chartData && (
          <Box
            sx={{
              width: {
                xs: '100%',
                md: chartData?.uiLayout === 'column' ? '50%' : '100%',
              },
              padding: '0 16px',
            }}
          >
            <BarChart {...chartData} />
          </Box>
        )}
        <Box
          sx={{
            width: {
              xs: '100%',
              md: chartData?.uiLayout === 'column' ? '50%' : '100%',
            },
            height: {
              xs: 'auto',
              md: dataGridHeight,
            },
          }}
          display='flex'
          flexDirection='column'
          alignItems='flex-end'
        >
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 20 },
              },
              filter: {
                filterModel: {
                  items: [],
                  quickFilterExcludeHiddenColumns: true,
                },
              },
            }}
            pageSizeOptions={[20, 100]}
            checkboxSelection
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            sx={{
              width: '100%',
              border: 'none',
              borderTop: {
                sm: chartData ? '1px solid #E0E0E0' : 'none',
                md: chartData?.uiLayout === 'row' ? '1px solid #E0E0E0' : 'none',
              },
              borderLeft: {
                sm: 'none',
                md: chartData?.uiLayout === 'column' ? '1px solid #E0E0E0' : 'none',
              },
              borderRadius: '0',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default PlanAdminReportsContent
